import {
    Alert,
    Badge,
    Button,
    CopyButton,
    Group,
    Modal,
    NumberInput,
    Paper,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title
} from "@mantine/core";
import {useMutation, useQuery} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {useContext, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api, {useEntityTypes} from "../../../utils/api.ts";
import {useProductionEstimate} from "../../../utils/production-estimate";
import {
    addMaterialTotals,
    buildAccessGroupKey,
    buildAccessibleMaterialTotals
} from "../../../utils/production-materials.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {
    ProductionManagementResponse,
    ProductionMaterialsResponse,
    ProductionPlannerBlock,
    ProductionPlannerPlanItem,
    ProductionPlannerRun,
    ProductionPlannerRunsResponse,
    ProductionTrackedEntity
} from "./types.ts";
import {
    BlockLocationIdModalData, BlockMaterialRow,
    BlockMaterialsSummary,
    BlockMetrics, LocationEstimateRow, PlannerPlanStatusSummary,
    PlannerProductCatalogByType,
    RunListSummary,
    RunValidation
} from "./plannerTypes.ts";
import {
    buildEmptyPlanStatusSummary,
    buildIdMap,
    cloneRun,
    filterPlannerItems,
    formatDurationRange,
    formatEtaWindow,
    getBlockKey,
    getEntityTypeName,
    getLocationStatusDisplay,
    getLocationSupportError,
    getRunSortOrder,
    newBlock,
    normalizeManagementSkill,
    splitBlockLocationIds
} from "./plannerUtils.ts";
import ProductionPlannerBlockCard from "./components/ProductionPlannerBlockCard.tsx";
import ProductionPlannerRunsTable from "./components/ProductionPlannerRunsTable.tsx";
import classes from "./ProductionModulePlannerPage.module.css";
import MessageContext from "../../../context/MessageContext.tsx";

function ProductionModulePlannerPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const moduleInfo = useProductionModule();
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    const [draftRun, setDraftRun] = useState<ProductionPlannerRun | null>(null);
    const [blockMaterialsOpen, setBlockMaterialsOpen] = useState<Record<string, boolean>>({});
    const [locationIdsModalBlockKey, setLocationIdsModalBlockKey] = useState<string | null>(null);
    const [newRunName, setNewRunName] = useState("New Run");
    const [newRunManagementSkill, setNewRunManagementSkill] = useState<number>(0);

    const runsQuery = useQuery<ProductionPlannerRunsResponse>({
        queryKey: ["prod-module", moduleId, "planner-runs"],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/planner/runs`)).data as ProductionPlannerRunsResponse,
        enabled: !!moduleId,
    });

    const managementQuery = useQuery<ProductionManagementResponse>({
        queryKey: ["prod-module", moduleId, "management"],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/management`)).data as ProductionManagementResponse,
        enabled: !!moduleId,
    });
    const materialsQuery = useQuery<ProductionMaterialsResponse>({
        queryKey: ["prod-module", moduleId, "materials"],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/materials`)).data as ProductionMaterialsResponse,
        enabled: !!moduleId,
    });

    const {estimateQueuedItem} = useProductionEstimate();
    const {error} = useContext(MessageContext);
    const {ships, vehicles, items, droids, materials, stations} = useEntityTypes(["ships", "vehicles", "items", "droids", "materials", "stations"]);
    const moduleMaterials = materialsQuery.data?.materials ?? [];
    const materialsReady = !!materialsQuery.data && !materialsQuery.isError;

    const createRunMutation = useMutation({
        mutationFn: async () => {
            return (await api.post(`/modules/production/${moduleId}/planner/runs`, {
                name: newRunName.trim() || "New Run",
                managementSkill: normalizeManagementSkill(newRunManagementSkill),
                blocks: [],
            })).data as {run: ProductionPlannerRun};
        },
        onSuccess: async (data) => {
            await runsQuery.refetch();
            if (data?.run?.id) {
                setSelectedRunId(data.run.id);
            }
        },
    });

    const saveRunMutation = useMutation({
        mutationFn: async () => {
            if (!draftRun) {
                return null;
            }
            return (await api.post(`/modules/production/${moduleId}/planner/runs/${draftRun.id}`, {
                name: draftRun.name,
                managementSkill: normalizeManagementSkill(draftRun.managementSkill),
                blocks: draftRun.blocks,
            })).data as {run: ProductionPlannerRun};
        },
        onSuccess: async (data) => {
            await runsQuery.refetch();
            if (data?.run) {
                setDraftRun(cloneRun(data.run));
            }
        },
        onError: async (err) => {
            error('Error', err.message)
        }
    });

    const deleteRunMutation = useMutation({
        mutationFn: async () => {
            if (!draftRun) {
                return null;
            }
            return (await api.delete(`/modules/production/${moduleId}/planner/runs/${draftRun.id}`)).data;
        },
        onSuccess: async () => {
            await runsQuery.refetch();
            setSelectedRunId(null);
            setDraftRun(null);
        },
    });
    const reorderRunsMutation = useMutation({
        mutationFn: async (runIds: string[]) => {
            return (await api.post(`/modules/production/${moduleId}/planner/runs/reorder`, {runIds})).data as ProductionPlannerRunsResponse;
        },
        onSuccess: async () => {
            await runsQuery.refetch();
        },
    });

    const runs = runsQuery.data?.runs ?? [];
    const entities = managementQuery.data?.entities ?? [];
    const moduleGroups = managementQuery.data?.groups ?? [];

    useEffect(() => {
        if (!selectedRunId) {
            return;
        }
        if (!runs.some((run) => run.id === selectedRunId)) {
            setSelectedRunId(null);
            setDraftRun(null);
        }
    }, [runs, selectedRunId]);

    useEffect(() => {
        if (!selectedRunId) {
            setDraftRun(null);
            return;
        }
        const selected = runs.find((run) => run.id === selectedRunId);
        if (!selected) {
            return;
        }
        setDraftRun(cloneRun(selected));
    }, [selectedRunId, runs]);

    const plannerItems = useMemo(() => filterPlannerItems(items), [items]);
    const stationTypeById = useMemo(() => buildIdMap(stations), [stations]);
    const productCatalogByType = useMemo<PlannerProductCatalogByType>(() => ({
        ship: buildIdMap(ships),
        vehicle: buildIdMap(vehicles),
        item: buildIdMap(plannerItems),
        droid: buildIdMap(droids),
    }), [ships, vehicles, plannerItems, droids]);

    const materialById = useMemo(() => new Map(materials.map((row) => [row.id, row])), [materials]);

    const entityById = useMemo(() => {
        const map = new Map<string, ProductionTrackedEntity>();
        entities.forEach((entity) => map.set(entity.entityId, entity));
        return map;
    }, [entities]);

    const runLocationIds = useMemo(() => {
        if (!draftRun) {
            return [] as string[];
        }
        const seen = new Set<string>();
        draftRun.blocks.forEach((block) => {
            (block.locationEntityIds ?? []).forEach((locationId) => {
                if (locationId) {
                    seen.add(locationId);
                }
            });
        });
        return Array.from(seen);
    }, [draftRun]);

    const locationMaterialTotals = useMemo(() => {
        const totals = new Map<string, Record<string, number>>();
        if (!materialsReady) {
            return totals;
        }

        runLocationIds.forEach((locationId) => {
            const entity = entityById.get(locationId);
            if (!entity) {
                return;
            }
            totals.set(locationId, buildAccessibleMaterialTotals(entity, moduleMaterials));
        });

        return totals;
    }, [runLocationIds, entityById, moduleMaterials, materialsReady]);

    const sortedRuns = useMemo(() => [...runs].sort((a, b) => {
        const sortDiff = getRunSortOrder(a) - getRunSortOrder(b);
        if (sortDiff !== 0) {
            return sortDiff;
        }
        if (b.updatedAt !== a.updatedAt) {
            return b.updatedAt - a.updatedAt;
        }
        return a.name.localeCompare(b.name);
    }), [runs]);
    const runListNowSeconds = Math.floor(DateTime.now().toSeconds());

    const runListSummaries = useMemo(() => {
        const nowSeconds = Math.floor(DateTime.now().toSeconds());
        const summaries = new Map<string, RunListSummary>();

        runs.forEach((run) => {
            const productTotals = new Map<string, {label: string; totalUnits: number}>();
            const blockMaxEnds: number[] = [];
            let etaUnknown = false;

            run.blocks.forEach((block) => {
                const quantity = Math.max(1, Number(block.quantityPerLocation || 1));
                const repeat = Math.max(1, Number(block.repeat || 1));
                const locationIds = block.locationEntityIds ?? [];
                const totalUnits = quantity * repeat * locationIds.length;

                const catalogEntry = block.productId
                    ? productCatalogByType[block.productType]?.get(block.productId) ?? null
                    : null;
                const productLabel = catalogEntry?.name ?? block.productId ?? `Unknown ${block.productType}`;
                const productKey = `${block.productType}:${block.productId || productLabel}`;

                if (totalUnits > 0) {
                    const existingProduct = productTotals.get(productKey);
                    if (existingProduct) {
                        existingProduct.totalUnits += totalUnits;
                    } else {
                        productTotals.set(productKey, {label: productLabel, totalUnits});
                    }
                }

                if (locationIds.length === 0) {
                    return;
                }
                if (!catalogEntry) {
                    etaUnknown = true;
                    return;
                }

                const locationMaxEnds: number[] = [];

                locationIds.forEach((locationId) => {
                    const location = entityById.get(locationId);
                    if (!location) {
                        etaUnknown = true;
                        return;
                    }

                    let startMin = nowSeconds;
                    let startMax = nowSeconds;
                    let failed = false;

                    for (let i = 0; i < repeat; i += 1) {
                        const estimate = estimateQueuedItem(
                            {quantity, entityId: block.productId},
                            location.entityType,
                            (location.location ?? {}) as Record<string, string>,
                            startMin,
                            startMax,
                            normalizeManagementSkill((run as {managementSkill?: unknown}).managementSkill)
                        );

                        if (estimate.estimate.status !== "ok") {
                            failed = true;
                            break;
                        }

                        startMin = estimate.nextStartMinAt;
                        startMax = estimate.nextStartMaxAt;
                    }

                    if (failed) {
                        etaUnknown = true;
                        return;
                    }

                    locationMaxEnds.push(startMax);
                });

                if (locationMaxEnds.length === 0) {
                    etaUnknown = true;
                    return;
                }

                blockMaxEnds.push(Math.max(...locationMaxEnds));
            });

            const products = Array.from(productTotals.entries())
                .map(([key, value]) => ({
                    key,
                    label: value.label,
                    totalUnits: value.totalUnits,
                }))
                .sort((a, b) => {
                    if (b.totalUnits !== a.totalUnits) {
                        return b.totalUnits - a.totalUnits;
                    }
                    return a.label.localeCompare(b.label);
                });

            const etaMaxAt = !etaUnknown && blockMaxEnds.length > 0
                ? Math.max(...blockMaxEnds)
                : null;

            summaries.set(run.id, {
                products,
                etaMaxAt,
                etaUnknown,
            });
        });

        return summaries;
    }, [runs, productCatalogByType, entityById, estimateQueuedItem]);

    const sanitizeBlockLocations = (
        blocks: ProductionPlannerBlock[],
        blockIndex: number,
        candidate: ProductionPlannerBlock
    ): string[] => {
        const uniqueIds = Array.from(
            new Set((candidate.locationEntityIds ?? []).map((id) => id.trim()).filter((id) => id !== ""))
        );
        const product = candidate.productId
            ? productCatalogByType[candidate.productType]?.get(candidate.productId) ?? null
            : null;

        return uniqueIds.filter((locationId) => {
            const usedElsewhere = blocks.some((other, otherIndex) => {
                if (otherIndex === blockIndex) {
                    return false;
                }
                return (other.locationEntityIds ?? []).includes(locationId);
            });
            if (usedElsewhere) {
                return false;
            }

            const location = entityById.get(locationId);
            if (!location) {
                return false;
            }

            if (!product) {
                return true;
            }

            return !getLocationSupportError(location, candidate.productType, product, stationTypeById);
        });
    };

    const updateBlock = (blockIndex: number, updater: (block: ProductionPlannerBlock) => ProductionPlannerBlock) => {
        setDraftRun((current) => {
            if (!current) {
                return current;
            }
            const nextBlocks = current.blocks.map((block, index) => (
                index === blockIndex ? updater(block) : block
            ));
            const candidate = nextBlocks[blockIndex];
            if (candidate) {
                nextBlocks[blockIndex] = {
                    ...candidate,
                    locationEntityIds: sanitizeBlockLocations(nextBlocks, blockIndex, candidate),
                };
            }
            return {...current, blocks: nextBlocks};
        });
    };

    const runValidation = useMemo<RunValidation>(() => {
        if (!draftRun) {
            return {runErrors: [], blockErrors: []};
        }

        const blockErrors: string[][] = draftRun.blocks.map(() => []);
        const runErrors: string[] = [];

        if (draftRun.name.trim() === "") {
            runErrors.push("Run name is required");
        }
        if (!Number.isFinite(draftRun.managementSkill) || draftRun.managementSkill < 0 || draftRun.managementSkill > 5) {
            runErrors.push("Management skill must be between 0 and 5");
        }

        const locationUsage = new Map<string, number>();

        draftRun.blocks.forEach((block, blockIndex) => {
            const errors = blockErrors[blockIndex];
            const product = block.productId ? productCatalogByType[block.productType]?.get(block.productId) ?? null : null;

            if (!product) {
                errors.push("Select a valid product");
            }
            if (!Number.isFinite(block.quantityPerLocation) || block.quantityPerLocation < 1 || block.quantityPerLocation > 46) {
                errors.push("Quantity per location must be between 1 and 46");
            }
            if (!Number.isFinite(block.repeat) || block.repeat < 1) {
                errors.push("Repeat must be at least 1");
            }
            if ((block.locationEntityIds ?? []).length === 0) {
                errors.push("Select at least one production location");
            }

            (block.locationEntityIds ?? []).forEach((locationId) => {
                const existingIndex = locationUsage.get(locationId);
                if (typeof existingIndex === "number" && existingIndex !== blockIndex) {
                    errors.push(`Location ${locationId} is already used in another block`);
                    return;
                }
                locationUsage.set(locationId, blockIndex);

                const location = entityById.get(locationId);
                if (!location) {
                    errors.push(`Location ${locationId} is not tracked in this module`);
                    return;
                }

                const supportError = getLocationSupportError(location, block.productType, product, stationTypeById);
                if (supportError) {
                    errors.push(`${location.name}: ${supportError}`);
                }
            });
        });

        return {runErrors, blockErrors};
    }, [draftRun, productCatalogByType, entityById, stationTypeById]);

    const blockMetrics = useMemo<BlockMetrics[]>(() => {
        if (!draftRun) {
            return [];
        }

        const nowSeconds = Math.floor(DateTime.now().toSeconds());

        return draftRun.blocks.map((block) => {
            const catalogEntry = block.productId ? productCatalogByType[block.productType]?.get(block.productId) ?? null : null;
            const locationIds = block.locationEntityIds ?? [];
            const quantity = Math.max(1, Number(block.quantityPerLocation || 1));
            const repeat = Math.max(1, Number(block.repeat || 1));
            const totalUnits = quantity * repeat * locationIds.length;

            const materialsNeeded: Record<string, number> = {};
            if (catalogEntry) {
                Object.entries(catalogEntry.materials ?? {}).forEach(([materialId, value]) => {
                    const base = Number(value || 0);
                    if (base > 0) {
                        materialsNeeded[materialId] = base * totalUnits;
                    }
                });
            }

            const locations: LocationEstimateRow[] = [];
            const endMins: number[] = [];
            const endMaxs: number[] = [];
            let etaUnknown = false;

            locationIds.forEach((locationId) => {
                const location = entityById.get(locationId);
                const status = location ? getLocationStatusDisplay(location) : {label: "Unknown", color: "gray"};
                if (!location || !catalogEntry) {
                    etaUnknown = true;
                    locations.push({
                        locationId,
                        locationName: location?.name ?? locationId,
                        locationTypeName: location ? getEntityTypeName(location) : "-",
                        statusLabel: status.label,
                        statusColor: status.color,
                        estimateText: "Unknown",
                        estimateUnknown: true,
                        etaMin: null,
                        etaMax: null,
                    });
                    return;
                }

                let startMin = nowSeconds;
                let startMax = nowSeconds;
                let failed = false;

                for (let i = 0; i < repeat; i += 1) {
                    const estimate = estimateQueuedItem(
                        {quantity, entityId: block.productId},
                        location.entityType,
                        (location.location ?? {}) as Record<string, string>,
                        startMin,
                        startMax,
                        draftRun.managementSkill
                    );

                    if (estimate.estimate.status !== "ok") {
                        failed = true;
                        break;
                    }

                    startMin = estimate.nextStartMinAt;
                    startMax = estimate.nextStartMaxAt;
                }

                if (failed) {
                    etaUnknown = true;
                    locations.push({
                        locationId,
                        locationName: location.name,
                        locationTypeName: getEntityTypeName(location),
                        statusLabel: status.label,
                        statusColor: status.color,
                        estimateText: "Unknown",
                        estimateUnknown: true,
                        etaMin: null,
                        etaMax: null,
                    });
                    return;
                }

                endMins.push(startMin);
                endMaxs.push(startMax);
                locations.push({
                    locationId,
                    locationName: location.name,
                    locationTypeName: getEntityTypeName(location),
                    statusLabel: status.label,
                    statusColor: status.color,
                    estimateText: formatDurationRange(startMin, startMax, nowSeconds),
                    estimateUnknown: false,
                    etaMin: startMin,
                    etaMax: startMax,
                });
            });

            let etaMin: number | null = null;
            let etaMax: number | null = null;

            if (!etaUnknown && endMins.length > 0 && endMaxs.length > 0) {
                etaMin = Math.max(...endMins);
                etaMax = Math.max(...endMaxs);
            } else if (locationIds.length > 0) {
                etaUnknown = true;
            }

            return {
                totalUnits,
                materials: materialsNeeded,
                etaMin,
                etaMax,
                etaUnknown,
                locations,
            };
        });
    }, [draftRun, productCatalogByType, entityById, estimateQueuedItem]);

    const blockMaterials = useMemo<BlockMaterialsSummary[]>(() => {
        if (!draftRun) {
            return [];
        }

        return draftRun.blocks.map((block, blockIndex) => {
            const requiredTotals = blockMetrics[blockIndex]?.materials ?? {};
            const rows = Object.entries(requiredTotals)
                .filter(([, quantity]) => quantity > 0)
                .map(([materialId, quantity]) => ({
                    materialId,
                    materialName: materialById.get(materialId)?.name ?? materialId,
                    quantity,
                }))
                .sort((a, b) => a.materialName.localeCompare(b.materialName));

            if (rows.length === 0) {
                return {rows: [], missingTypes: 0, missingWeight: null, missingVolume: null};
            }

            const availableByMaterial: Record<string, number> = {};
            if (materialsReady) {
                const seenAccessGroups = new Set<string>();
                (block.locationEntityIds ?? []).forEach((locationId) => {
                    const entity = entityById.get(locationId);
                    if (!entity) {
                        return;
                    }
                    const accessKey = buildAccessGroupKey(entity);
                    if (seenAccessGroups.has(accessKey)) {
                        return;
                    }
                    seenAccessGroups.add(accessKey);

                    const locationTotals = locationMaterialTotals.get(locationId);
                    if (!locationTotals) {
                        return;
                    }
                    addMaterialTotals(availableByMaterial, locationTotals);
                });
            }

            const withAvailability: BlockMaterialRow[] = rows.map((row) => {
                const available = materialsReady ? (availableByMaterial[row.materialId] ?? 0) : null;
                const missing = materialsReady && available !== null
                    ? Math.max(0, row.quantity - available)
                    : null;
                return {
                    materialId: row.materialId,
                    materialName: row.materialName,
                    quantity: row.quantity,
                    available,
                    missing,
                };
            });

            const missingTypes = withAvailability.filter((row) => (row.missing ?? 0) > 0).length;

            // Cargo is based on what still has to be hauled in, unknown if availability or weight/volume data is missing
            let missingWeight: number | null = materialsReady ? 0 : null;
            let missingVolume: number | null = materialsReady ? 0 : null;
            withAvailability.forEach((row) => {
                if (!row.missing) {
                    return;
                }
                const material = materialById.get(row.materialId);
                const hasCargoData = material && Number.isFinite(material.weight) && Number.isFinite(material.volume);
                missingWeight = hasCargoData && missingWeight !== null ? missingWeight + row.missing * material.weight : null;
                missingVolume = hasCargoData && missingVolume !== null ? missingVolume + row.missing * material.volume : null;
            });

            return {
                rows: withAvailability,
                missingTypes,
                missingWeight,
                missingVolume,
            };
        });
    }, [draftRun, blockMetrics, materialById, materialsReady, entityById, locationMaterialTotals]);

    const blockLocationPlanStatuses = useMemo(() => {
        const statusMap = new Map<string, PlannerPlanStatusSummary>();
        if (!draftRun) {
            return statusMap;
        }

        (draftRun.planItems ?? []).forEach((planItem: ProductionPlannerPlanItem) => {
            const key = `${planItem.blockId}:${planItem.locationEntityId}`;
            const summary = statusMap.get(key) ?? buildEmptyPlanStatusSummary();
            summary.total += 1;
            if (planItem.status === "active" || planItem.status === "queued" || planItem.status === "completed") {
                summary[planItem.status] += 1;
            } else {
                summary.pending += 1;
            }
            statusMap.set(key, summary);
        });

        return statusMap;
    }, [draftRun]);

    useEffect(() => {
        if (!draftRun) {
            setBlockMaterialsOpen((current) => (
                Object.keys(current).length === 0 ? current : {}
            ));
            return;
        }

        setBlockMaterialsOpen((current) => {
            const next: Record<string, boolean> = {};
            draftRun.blocks.forEach((block, blockIndex) => {
                const key = getBlockKey(block, blockIndex);
                const hasMissing = (blockMaterials[blockIndex]?.missingTypes ?? 0) > 0;
                next[key] = Object.prototype.hasOwnProperty.call(current, key)
                    ? !!current[key]
                    : hasMissing;
            });

            const currentKeys = Object.keys(current);
            const nextKeys = Object.keys(next);
            if (currentKeys.length === nextKeys.length && nextKeys.every((key) => current[key] === next[key])) {
                return current;
            }

            return next;
        });
    }, [draftRun, blockMaterials]);

    const runEta = useMemo(() => {
        if (!draftRun || draftRun.blocks.length === 0) {
            return {min: null as number | null, max: null as number | null, unknown: false};
        }

        let unknown = false;
        const mins: number[] = [];
        const maxs: number[] = [];

        blockMetrics.forEach((metrics) => {
            if (metrics.etaUnknown) {
                unknown = true;
                return;
            }
            if (metrics.etaMin && metrics.etaMax) {
                mins.push(metrics.etaMin);
                maxs.push(metrics.etaMax);
            }
        });

        if (unknown) {
            return {min: null, max: null, unknown: true};
        }
        if (mins.length === 0 || maxs.length === 0) {
            return {min: null, max: null, unknown: false};
        }

        return {
            min: Math.max(...mins),
            max: Math.max(...maxs),
            unknown: false,
        };
    }, [draftRun, blockMetrics]);

    const canSave = !!draftRun
        && runValidation.runErrors.length === 0
        && runValidation.blockErrors.every((errors) => errors.length === 0);

    const locationIdsModalData = useMemo<BlockLocationIdModalData | null>(() => {
        if (!draftRun || !locationIdsModalBlockKey) {
            return null;
        }

        const blockIndex = draftRun.blocks.findIndex((block, index) => getBlockKey(block, index) === locationIdsModalBlockKey);
        if (blockIndex === -1) {
            return null;
        }

        const block = draftRun.blocks[blockIndex];
        const ids = splitBlockLocationIds(block.locationEntityIds ?? []);
        return {
            title: `Block #${blockIndex + 1} Location IDs`,
            facilities: ids.facilities,
            stations: ids.stations,
        };
    }, [draftRun, locationIdsModalBlockKey]);

    useEffect(() => {
        if (locationIdsModalBlockKey && !locationIdsModalData) {
            setLocationIdsModalBlockKey(null);
        }
    }, [locationIdsModalBlockKey, locationIdsModalData]);

    const moveRun = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= sortedRuns.length) {
            return;
        }
        const next = [...sortedRuns];
        const [moved] = next.splice(fromIndex, 1);
        if (!moved) {
            return;
        }
        next.splice(toIndex, 0, moved);
        reorderRunsMutation.mutate(next.map((run) => run.id));
    };

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }

    if (runsQuery.isPending || managementQuery.isPending) {
        return <LoaderScreen message="Loading planner"/>;
    }

    if (runsQuery.isError || managementQuery.isError) {
        return <Page><Alert color="red" title="Planner unavailable">Could not load planner data.</Alert></Page>;
    }

    const isDetailsView = !!selectedRunId && !!draftRun;

    return (
        <Page>
            <title>{`${moduleInfo?.label || 'Production'} - Planner`}</title>
            <Stack gap="md">
                <Title order={1}>{moduleInfo?.label || 'Production'} - Planner</Title>
                <ProductionModuleNav moduleId={moduleId}/>

                {!isDetailsView ? (
                    <>
                        <Paper withBorder p="md">
                            <Group align="end">
                                <TextInput
                                    className={classes.growInput}
                                    label="New run name"
                                    value={newRunName}
                                    onChange={(event) => setNewRunName(event.currentTarget.value)}
                                />
                                <NumberInput
                                    className={classes.managementField}
                                    label="Management skill"
                                    min={0}
                                    max={5}
                                    step={1}
                                    value={newRunManagementSkill}
                                    onChange={(value) => setNewRunManagementSkill(normalizeManagementSkill(value))}
                                />
                                <Button loading={createRunMutation.isPending} onClick={() => createRunMutation.mutate()}>
                                    Create Run
                                </Button>
                            </Group>
                        </Paper>

                        <ProductionPlannerRunsTable
                            runs={sortedRuns}
                            summaries={runListSummaries}
                            nowSeconds={runListNowSeconds}
                            reorderPending={reorderRunsMutation.isPending}
                            onMove={moveRun}
                            onOpen={setSelectedRunId}
                        />
                    </>
                ) : (
                    <>
                        <Paper withBorder p="md">
                            <Group justify="space-between" align="center">
                                <Button variant="light" onClick={() => setSelectedRunId(null)}>
                                    Back To Runs
                                </Button>
                                <Group gap="xs">
                                    <Badge color="teal" variant="light">
                                        ETA: {runEta.unknown ? "Unknown" : formatEtaWindow(runEta.min, runEta.max)}
                                    </Badge>
                                    <Badge color="blue" variant="light">
                                        Blocks: {draftRun.blocks.length}
                                    </Badge>
                                </Group>
                            </Group>

                            <Group align="end" mt="md">
                                <TextInput
                                    className={classes.growInput}
                                    label="Run name"
                                    value={draftRun.name}
                                    onChange={(event) => setDraftRun({...draftRun, name: event.currentTarget.value})}
                                />
                                <NumberInput
                                    className={classes.managementField}
                                    label="Management skill"
                                    min={0}
                                    max={5}
                                    step={1}
                                    value={draftRun.managementSkill}
                                    onChange={(value) => setDraftRun({...draftRun, managementSkill: normalizeManagementSkill(value)})}
                                />
                                <Button
                                    loading={saveRunMutation.isPending}
                                    disabled={!canSave}
                                    onClick={() => saveRunMutation.mutate()}
                                >
                                    Save Run
                                </Button>
                                <Button
                                    loading={deleteRunMutation.isPending}
                                    color="red"
                                    variant="light"
                                    onClick={() => deleteRunMutation.mutate()}
                                >
                                    Delete Run
                                </Button>
                            </Group>

                            {runValidation.runErrors.length > 0 ? (
                                <Alert color="red" mt="sm" title="Run validation">
                                    {runValidation.runErrors.join(" | ")}
                                </Alert>
                            ) : null}
                        </Paper>

                        <Paper withBorder p="md">
                            <Group align="center">
                                <Text fw={700}>Blocks</Text>
                            </Group>

                            <Stack gap="sm" mt="sm">
                                {draftRun.blocks.map((block, blockIndex) => {
                                    const blockKey = getBlockKey(block, blockIndex);
                                    return (
                                        <ProductionPlannerBlockCard
                                            key={block.id ?? `block-${blockIndex}`}
                                            block={block}
                                            blockIndex={blockIndex}
                                            entities={entities}
                                            moduleGroups={moduleGroups}
                                            metrics={blockMetrics[blockIndex]}
                                            materialSummary={blockMaterials[blockIndex] ?? {rows: [], missingTypes: 0, missingWeight: null, missingVolume: null}}
                                            planStatuses={blockLocationPlanStatuses}
                                            productCatalogByType={productCatalogByType}
                                            stationTypeById={stationTypeById}
                                            validationErrors={runValidation.blockErrors[blockIndex] ?? []}
                                            isMaterialsOpen={blockMaterialsOpen[blockKey] ?? ((blockMaterials[blockIndex]?.missingTypes ?? 0) > 0)}
                                            materialsReady={materialsReady}
                                            isLocationUsedElsewhere={(locationId) => (
                                                draftRun.blocks.some((other, otherIndex) => (
                                                    otherIndex !== blockIndex && (other.locationEntityIds ?? []).includes(locationId)
                                                ))
                                            )}
                                            onUpdate={(updater) => updateBlock(blockIndex, updater)}
                                            onOpenLocationIds={() => setLocationIdsModalBlockKey(blockKey)}
                                            onRemove={() => setDraftRun({
                                                ...draftRun,
                                                blocks: draftRun.blocks.filter((_, index) => index !== blockIndex),
                                            })}
                                            onToggleMaterials={() => {
                                                setBlockMaterialsOpen((current) => ({
                                                    ...current,
                                                    [blockKey]: !(current[blockKey] ?? ((blockMaterials[blockIndex]?.missingTypes ?? 0) > 0)),
                                                }));
                                            }}
                                        />
                                    );
                                })}

                                {draftRun.blocks.length === 0 ? (
                                    <Text size="sm" c="dimmed">No blocks yet. Add a block to start planning.</Text>
                                ) : null}

                                <Paper withBorder p="sm" className={classes.addBlockCard}>
                                    <Button
                                        variant="subtle"
                                        fullWidth
                                        className={classes.addBlockButton}
                                        onClick={() => setDraftRun({...draftRun, blocks: [...draftRun.blocks, newBlock()]})}
                                    >
                                        Add Block
                                    </Button>
                                </Paper>
                            </Stack>
                        </Paper>
                    </>
                )}
            </Stack>

            <Modal
                opened={!!locationIdsModalData}
                onClose={() => setLocationIdsModalBlockKey(null)}
                title={locationIdsModalData?.title ?? "Block Location IDs"}
                size="sm"
            >
                <Stack gap="md">
                    {([
                        {key: "facilities", label: "Facilities", ids: locationIdsModalData?.facilities ?? []},
                        {key: "stations", label: "Stations", ids: locationIdsModalData?.stations ?? []},
                    ] as const).map((section) => {
                        const copyValue = section.ids.join(",");
                        return (
                            <Stack key={section.key} gap="xs">
                                <Group justify="space-between" align="center">
                                    <Text fw={600}>{section.label}</Text>
                                    <CopyButton value={copyValue} timeout={1500}>
                                        {({copied, copy}) => (
                                            <Button
                                                size="compact-xs"
                                                variant="light"
                                                disabled={section.ids.length === 0}
                                                onClick={copy}
                                            >
                                                {copied ? "Copied" : "Copy"}
                                            </Button>
                                        )}
                                    </CopyButton>
                                </Group>
                                <Textarea
                                    readOnly
                                    autosize
                                    minRows={3}
                                    maxRows={8}
                                    value={copyValue}
                                    placeholder={`No ${section.label.toLowerCase()} selected`}
                                    classNames={{input: classes.locationIdsTextarea}}
                                />
                            </Stack>
                        );
                    })}
                </Stack>
            </Modal>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModulePlannerPage);
